import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Vul je e-mailadres in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/affiliate/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        toast.error(data.detail || "Er ging iets mis");
      }
    } catch (error) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Proofix</span>
          </Link>
          <Link to="/partner-login">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Terug naar inloggen
            </Button>
          </Link>
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {!isSubmitted ? (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-2xl flex items-center justify-center border border-blue-500/30">
                  <Mail className="w-8 h-8 text-blue-400" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-white text-center mb-2">
                Wachtwoord vergeten?
              </h1>
              <p className="text-slate-400 text-center mb-8">
                Geen probleem! Vul je e-mailadres in en we sturen je een link om je wachtwoord te resetten.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="bg-slate-900/50 rounded-2xl border border-slate-800 p-6 space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-300 font-medium">
                      E-mailadres
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jouw@email.nl"
                      className="mt-2 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                      data-testid="forgot-password-email"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20"
                  data-testid="forgot-password-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Bezig...
                    </>
                  ) : (
                    <>
                      Resetlink aanvragen
                      <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-white mb-2">
                Check je inbox!
              </h1>
              <p className="text-slate-400 mb-8">
                Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele minuten een resetlink.
              </p>

              <div className="bg-slate-900/50 rounded-xl border border-slate-800 p-4 mb-6">
                <p className="text-slate-300 text-sm">
                  <strong>Tip:</strong> Check ook je spam folder als je de e-mail niet ziet.
                </p>
              </div>

              <Link to="/partner-login">
                <Button
                  variant="outline"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug naar inloggen
                </Button>
              </Link>
            </div>
          )}

          {/* Back to login link */}
          {!isSubmitted && (
            <p className="text-center text-slate-500 text-sm mt-6">
              Weet je je wachtwoord weer?{" "}
              <Link to="/partner-login" className="text-blue-400 hover:text-blue-300 font-medium">
                Inloggen
              </Link>
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
